import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Plus, Bell, Send, Users, Crown, User, Star } from 'lucide-react';
import api from '../lib/api';

export default function AdminNotificationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newNotif, setNewNotif] = useState({
    title: '',
    message: '',
    target: 'all'
  });

  useEffect(() => {
    if (!user?.admin_role) {
      navigate('/dashboard');
      return;
    }
    fetchNotifications();
  }, [user, navigate]);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/admin/notifications');
      setNotifications(response.data.notifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!newNotif.title || !newNotif.message) return;
    try {
      await api.post('/admin/notifications', newNotif);
      setShowCreateModal(false);
      setNewNotif({ title: '', message: '', target: 'all' });
      fetchNotifications();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur');
    }
  };

  const getTargetIcon = (target) => {
    switch (target) {
      case 'premium': return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'standard': return <Star className="w-4 h-4 text-blue-500" />;
      case 'free': return <User className="w-4 h-4 text-gray-500" />;
      default: return <Users className="w-4 h-4 text-blue-500" />;
    }
  };

  const getTargetLabel = (target) => {
    switch (target) {
      case 'premium': return 'Premium';
      case 'standard': return 'Standard';
      case 'free': return 'Gratuit';
      default: return 'Tous';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Notifications</h1>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Send className="w-4 h-4 mr-2" />
            Envoyer
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          </div>
        ) : notifications.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucune notification envoyée</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {notifications.map((notif) => (
              <Card key={notif.notification_id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mt-1">
                        <Bell className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{notif.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{notif.message}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            {getTargetIcon(notif.target)}
                            <span>{getTargetLabel(notif.target)}</span>
                          </div>
                          <span>{new Date(notif.created_at).toLocaleDateString('fr-FR')}</span>
                          <span>{notif.read_by?.length || 0} lu(s)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Envoyer une Notification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Titre</label>
              <Input
                value={newNotif.title}
                onChange={(e) => setNewNotif({ ...newNotif, title: e.target.value })}
                placeholder="Titre de la notification"
                className="mt-2"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Message</label>
              <textarea
                value={newNotif.message}
                onChange={(e) => setNewNotif({ ...newNotif, message: e.target.value })}
                placeholder="Contenu du message..."
                className="mt-2 w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Destinataires</label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {[
                  { value: 'all', label: 'Tous', icon: Users },
                  { value: 'premium', label: 'Premium', icon: Crown },
                  { value: 'standard', label: 'Standard', icon: Star },
                  { value: 'free', label: 'Gratuit', icon: User }
                ].map((opt) => (
                  <Button
                    key={opt.value}
                    variant={newNotif.target === opt.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNewNotif({ ...newNotif, target: opt.value })}
                  >
                    <opt.icon className="w-4 h-4 mr-1" />
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
            <Button onClick={handleSendNotification} disabled={!newNotif.title || !newNotif.message}>
              <Send className="w-4 h-4 mr-2" />
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
